/**
 * Durable Supabase auth session storage for browser and Capacitor clients.
 * - Prefer localStorage (survives browser restarts)
 * - Mirror to Capacitor Preferences on native (survives WebView quirks)
 * - Migrate legacy storage keys
 * - Never write sessions to sessionStorage (tab-scoped — lost when app closes)
 */

const PRIMARY_KEY = 'supabase-auth-token';
const LEGACY_KEY = 'supabase.auth.token';
const NATIVE_PREFS_KEY = 'tb:supabase-auth-session';

/** In-memory cache so sync getItem works after async native hydration. */
let memoryCache: Record<string, string> = {};

function isNativeCapacitor(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { Capacitor } = require('@capacitor/core') as { Capacitor?: { isNativePlatform?: () => boolean } };
    return Capacitor?.isNativePlatform?.() ?? false;
  } catch {
    return false;
  }
}

function projectRefFromUrl(supabaseUrl: string | undefined): string | null {
  if (!supabaseUrl) return null;
  try {
    const ref = new URL(supabaseUrl).hostname.split('.')[0];
    return ref || null;
  } catch {
    return null;
  }
}

export function buildAuthStorageKeys(supabaseUrl: string | undefined): string[] {
  const ref = projectRefFromUrl(supabaseUrl);
  const keys = [PRIMARY_KEY];
  if (ref) keys.push(`sb-${ref}-auth-token`);
  keys.push(LEGACY_KEY);
  return [...new Set(keys)];
}

function canUseLocalStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const probe = '__auth_storage_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function readRawFromBrowser(keys: string[]): { value: string | null; sourceKey: string | null } {
  for (const storage of [localStorage, sessionStorage]) {
    try {
      for (const key of keys) {
        const value = storage.getItem(key);
        if (value) return { value, sourceKey: key };
      }
    } catch {
      /* try next storage */
    }
  }
  return { value: null, sourceKey: null };
}

function readFromAnyKey(keys: string[]): { value: string | null; sourceKey: string | null } {
  for (const key of keys) {
    const cached = memoryCache[key];
    if (cached) return { value: cached, sourceKey: key };
  }
  return readRawFromBrowser(keys);
}

function writeToLocalStorage(key: string, value: string, aliasKeys: string[]): boolean {
  if (!canUseLocalStorage()) return false;
  try {
    localStorage.setItem(key, value);
    memoryCache[key] = value;
    for (const alias of aliasKeys) {
      if (alias === key) continue;
      try {
        localStorage.setItem(alias, value);
        memoryCache[alias] = value;
      } catch {
        /* ignore alias write failure */
      }
    }
    return true;
  } catch (error) {
    console.warn('[Auth] Failed to persist session to localStorage:', error);
    return false;
  }
}

function mirrorToNativePrefs(value: string): void {
  if (!isNativeCapacitor()) return;
  void import('@capacitor/preferences')
    .then(({ Preferences }) => Preferences.set({ key: NATIVE_PREFS_KEY, value }))
    .catch(() => {
      /* non-critical */
    });
}

function clearNativePrefs(): void {
  if (!isNativeCapacitor()) return;
  void import('@capacitor/preferences')
    .then(({ Preferences }) => Preferences.remove({ key: NATIVE_PREFS_KEY }))
    .catch(() => {
      /* non-critical */
    });
}

export interface ParsedPersistedSession {
  userId: string;
  email: string;
  expiresAt: number | null;
  hasRefreshToken: boolean;
}

/** Parse stored Supabase session JSON without a network call (offline / slow-network bootstrap). */
export function parsePersistedSupabaseSession(
  supabaseUrl: string | undefined,
): ParsedPersistedSession | null {
  try {
    const { value } = readFromAnyKey(buildAuthStorageKeys(supabaseUrl));
    if (!value) return null;

    const parsed = JSON.parse(value) as Record<string, unknown>;
    const session =
      (parsed.currentSession as Record<string, unknown> | undefined) ??
      (parsed.session as Record<string, unknown> | undefined) ??
      parsed;

    const user = session.user as Record<string, unknown> | undefined;
    const userId = typeof user?.id === 'string' ? user.id : null;
    if (!userId) return null;

    const email = typeof user?.email === 'string' ? user.email : '';
    const expiresAt =
      typeof session.expires_at === 'number'
        ? session.expires_at
        : typeof parsed.expiresAt === 'number'
          ? parsed.expiresAt
          : null;
    const hasRefreshToken = typeof session.refresh_token === 'string' && session.refresh_token.length > 0;

    return { userId, email, expiresAt, hasRefreshToken };
  } catch {
    return null;
  }
}

/**
 * Load session from Capacitor Preferences into localStorage before Supabase init.
 * Call once at app startup on native devices.
 */
export async function hydrateAuthStorageFromNative(supabaseUrl: string | undefined): Promise<void> {
  if (typeof window === 'undefined') return;

  const keys = buildAuthStorageKeys(supabaseUrl);
  const existing = readRawFromBrowser(keys);
  if (existing.value) {
    memoryCache[PRIMARY_KEY] = existing.value;
    return;
  }

  if (!isNativeCapacitor()) return;

  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key: NATIVE_PREFS_KEY });
    if (!value) return;

    writeToLocalStorage(PRIMARY_KEY, value, keys);
    // Migrate legacy sessionStorage copies into durable storage.
    for (const storage of [sessionStorage]) {
      try {
        for (const key of keys) {
          storage.removeItem(key);
        }
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* ignore — fall back to browser storage only */
  }
}

export function createSupabaseAuthStorage(supabaseUrl: string | undefined) {
  const keys = buildAuthStorageKeys(supabaseUrl);

  // Supabase stores more than the session here — notably the PKCE code verifier for
  // Google sign-in. Only the session may be mirrored across the legacy key aliases;
  // aliasing anything else overwrote the session and wiped it on cleanup, which broke
  // the OAuth code exchange entirely.
  const isSessionKey = (key: string) => keys.includes(key);

  const readSingleKey = (key: string): string | null => {
    const cached = memoryCache[key];
    if (cached) return cached;
    for (const storage of [localStorage, sessionStorage]) {
      try {
        const value = storage.getItem(key);
        if (value) return value;
      } catch {
        /* try next storage */
      }
    }
    return null;
  };

  return {
    getItem: (key: string) => {
      try {
        if (!isSessionKey(key)) return readSingleKey(key);

        const primary = readFromAnyKey([key, ...keys.filter((k) => k !== key)]);
        if (!primary.value) return null;

        // Migrate legacy / duplicate keys into the active storage key.
        if (primary.sourceKey && primary.sourceKey !== key) {
          writeToLocalStorage(
            key,
            primary.value,
            keys.filter((k) => k !== key),
          );
          if (primary.sourceKey !== key) {
            try {
              for (const legacy of keys) {
                if (legacy !== key) {
                  sessionStorage.removeItem(legacy);
                }
              }
            } catch {
              /* ignore */
            }
          }
        }

        return primary.value;
      } catch {
        return null;
      }
    },
    setItem: (key: string, value: string) => {
      memoryCache[key] = value;
      const wrote = writeToLocalStorage(key, value, isSessionKey(key) ? keys.filter((k) => k !== key) : []);
      if (wrote) {
        if (isSessionKey(key)) mirrorToNativePrefs(value);
        return;
      }

      // Last resort: memory only for this tab — warn loudly.
      console.warn(
        '[Auth] Session stored in memory only — login will not survive closing the app. Check storage permissions.',
      );
    },
    removeItem: (key: string) => {
      const aliases = isSessionKey(key) ? keys : [];
      delete memoryCache[key];
      for (const k of aliases) {
        delete memoryCache[k];
      }
      for (const storage of [localStorage, sessionStorage]) {
        try {
          storage.removeItem(key);
          for (const alias of aliases) {
            storage.removeItem(alias);
          }
        } catch {
          /* ignore */
        }
      }
      if (isSessionKey(key)) clearNativePrefs();
    },
  };
}

/** Remove Supabase auth keys without wiping unrelated sessionStorage (OAuth, impersonation, etc.). */
export function clearSupabaseAuthStorage(supabaseUrl: string | undefined): void {
  const storage = createSupabaseAuthStorage(supabaseUrl);
  for (const key of buildAuthStorageKeys(supabaseUrl)) {
    storage.removeItem(key);
  }
  memoryCache = {};
}

export async function verifyAuthSessionPersisted(): Promise<boolean> {
  try {
    const supabaseUrl =
      typeof import.meta !== 'undefined' && import.meta.env
        ? (import.meta.env.VITE_SUPABASE_URL as string | undefined)
        : undefined;
    const { value } = readFromAnyKey(buildAuthStorageKeys(supabaseUrl));
    if (value) return true;
    if (!isNativeCapacitor()) return false;
    const { Preferences } = await import('@capacitor/preferences');
    const { value: nativeValue } = await Preferences.get({ key: NATIVE_PREFS_KEY });
    return Boolean(nativeValue);
  } catch {
    return false;
  }
}

export function hasPersistedAuthSession(supabaseUrl: string | undefined): boolean {
  try {
    return Boolean(readFromAnyKey(buildAuthStorageKeys(supabaseUrl)).value);
  } catch {
    return false;
  }
}
